// ─── Interior tile constants ────────────────────────────────────────────────
export const INT_TILE = { FLOOR: 0, WALL: 1, DOOR: 2, DECOR: 3, STAIRS_UP: 4, STAIRS_DOWN: 5 };

// ─── Map builders ───────────────────────────────────────────────────────────

function buildHospitalMap() {
  const W = 22, H = 16;
  const F = INT_TILE.FLOOR, WL = INT_TILE.WALL, D = INT_TILE.DOOR, DC = INT_TILE.DECOR;
  const map = Array.from({ length: H }, () => Array(W).fill(F));

  // Border walls
  for (let c = 0; c < W; c++) { map[0][c] = WL; map[H - 1][c] = WL; }
  for (let r = 0; r < H; r++) { map[r][0] = WL; map[r][W - 1] = WL; }

  // Exit door at bottom
  map[H - 1][11] = D;

  // Vertical room dividers (cols 7 & 14, rows 0–6)
  for (let r = 0; r <= 6; r++) { map[r][7] = WL; map[r][14] = WL; }

  // South walls of patient rooms (row 6) — each room gets a door
  for (let c = 1; c <= 6;  c++) map[6][c] = WL;   // room 1 south wall
  for (let c = 8; c <= 13; c++) map[6][c] = WL;   // room 2 south wall
  for (let c = 15; c <= 20; c++) map[6][c] = WL;  // room 3 south wall
  map[6][4]  = F;   // room 1 opening
  map[6][11] = F;   // room 2 opening
  map[6][18] = F;   // room 3 opening

  // Corridor ↔ lobby divider (row 8) with a central opening
  for (let c = 1; c < W - 1; c++) map[8][c] = WL;
  map[8][11] = F;

  // Furniture footprints (DECOR = floor colour but non-walkable)
  // Beds — 2×2 per room
  [[2,1],[3,1],[2,2],[3,2],
   [9,1],[10,1],[9,2],[10,2],
   [15,1],[16,1],[15,2],[16,2]].forEach(([c, r]) => { map[r][c] = DC; });

  // Room equipment
  [[5,1],[5,2]].forEach(([c, r]) => { map[r][c] = DC; }); // ultrasound (Room 1)

  // Lobby furniture
  [[9,10],[10,10],[11,10],[12,10],         // reception desk (center)
   [2,11],[3,11],[4,11],                  // waiting room chairs (left)
   [15,9],[16,9],                         // medicine shelf 2 (right)
   [18,9],[19,9],                         // medicine shelf 3 (far right)
   [19,1],                                // toilet (top-right corner of Room 3)
   [18,1],                                // shower (next to toilet)
  ].forEach(([c, r]) => { map[r][c] = DC; });

  // Stairs up to Floor 2 — bottom-left corner of lobby
  map[13][3] = INT_TILE.STAIRS_UP;

  return map;
}

function buildHospital2FMap() {
  const W = 22, H = 16;
  const F = INT_TILE.FLOOR, WL = INT_TILE.WALL, DC = INT_TILE.DECOR;
  const map = Array.from({ length: H }, () => Array(W).fill(F));

  // Border walls (no exit door — this floor has stairs only)
  for (let c = 0; c < W; c++) { map[0][c] = WL; map[H - 1][c] = WL; }
  for (let r = 0; r < H; r++) { map[r][0] = WL; map[r][W - 1] = WL; }

  // Same 3-room structure as floor 1
  for (let r = 0; r <= 6; r++) { map[r][7] = WL; map[r][14] = WL; }
  for (let c = 1; c <= 6;  c++) map[6][c] = WL;
  for (let c = 8; c <= 13; c++) map[6][c] = WL;
  for (let c = 15; c <= 20; c++) map[6][c] = WL;
  map[6][4]  = F;
  map[6][11] = F;
  map[6][18] = F;

  // Corridor ↔ ward divider
  for (let c = 1; c < W - 1; c++) map[8][c] = WL;
  map[8][11] = F;

  // Bed footprints (Room 1 only — Rooms 2 & 3 have equipment instead)
  [[2,1],[3,1],[2,2],[3,2]].forEach(([c, r]) => { map[r][c] = DC; });

  // X-ray machine in Room 2 (cols 11-12, rows 1-2)
  [[11,1],[12,1],[11,2],[12,2]].forEach(([c, r]) => { map[r][c] = DC; });

  // OP bed in Room 3 (cols 18-19, rows 1-2)
  [[18,1],[19,1],[18,2],[19,2]].forEach(([c, r]) => { map[r][c] = DC; });

  // Medicine shelves — along lobby top wall (row 9), gap at col 11 for corridor entry
  [[1,9],[2,9],[4,9],[5,9],[7,9],[8,9],[13,9],[14,9]].forEach(([c, r]) => { map[r][c] = DC; });

  // Nurse station counter (top-right of ward area)
  [[17,9],[18,9],[19,9],[17,10],[18,10],[19,10]].forEach(([c, r]) => { map[r][c] = DC; });

  // Stairs down back to Floor 1 — bottom-left corner (matches floor 1 position)
  map[13][3] = INT_TILE.STAIRS_DOWN;

  return map;
}

function buildPharmacyMap() {
  const W = 20, H = 14;
  const F = INT_TILE.FLOOR, WL = INT_TILE.WALL, D = INT_TILE.DOOR, DC = INT_TILE.DECOR;
  const map = Array.from({ length: H }, () => Array(W).fill(F));

  // Border walls
  for (let c = 0; c < W; c++) { map[0][c] = WL; map[H - 1][c] = WL; }
  for (let r = 0; r < H; r++) { map[r][0] = WL; map[r][W - 1] = WL; }

  // Exit door at bottom centre
  map[H - 1][10] = D;

  // Shelf units at rows 2–3 and 5–6
  const units = [[1, 4], [6, 10], [12, 17]];
  for (const row of [2, 3, 5, 6]) {
    units.forEach(([a, b]) => {
      for (let c = a; c <= b; c++) map[row][c] = WL;
    });
  }

  // Pharmacist reception desk (rows 8–9)
  [[6,8],[7,8],[8,8],[9,8],[6,9],[7,9],[8,9],[9,9]].forEach(([c, r]) => { map[r][c] = DC; });

  // Plant — left of reception
  map[8][5] = DC;

  return map;
}

function buildHotelMap() {
  const W = 22, H = 22;
  const F = INT_TILE.FLOOR, WL = INT_TILE.WALL, D = INT_TILE.DOOR, DC = INT_TILE.DECOR;
  const map = Array.from({ length: H }, () => Array(W).fill(F));

  // Border walls
  for (let c = 0; c < W; c++) { map[0][c] = WL; map[H - 1][c] = WL; }
  for (let r = 0; r < H; r++) { map[r][0] = WL; map[r][W - 1] = WL; }

  // Exit door at bottom centre
  map[H - 1][11] = D;

  // ── Top hotel rooms (rows 0–5): Rooms 101, 102, 103 ─────────────────────
  for (let r = 0; r <= 5; r++) { map[r][7] = WL; map[r][14] = WL; }
  for (let c = 1; c <= 6; c++)   map[5][c] = WL;    // Room 101 south wall
  for (let c = 8; c <= 13; c++)  map[5][c] = WL;    // Room 102 south wall
  for (let c = 15; c <= 20; c++) map[5][c] = WL;    // Room 103 south wall
  map[5][3] = F;   // Room 101 door
  map[5][10] = F;  // Room 102 door
  map[5][17] = F;  // Room 103 door

  // ── Bottom hotel rooms (rows 8–13): Rooms 201, 202, 203 ─────────────────
  for (let r = 8; r <= 13; r++) { map[r][7] = WL; map[r][14] = WL; }
  // North walls (row 8)
  for (let c = 1; c <= 6; c++)   map[8][c] = WL;
  for (let c = 8; c <= 13; c++)  map[8][c] = WL;
  for (let c = 15; c <= 20; c++) map[8][c] = WL;
  map[8][3] = F; map[8][10] = F; map[8][17] = F;
  // South walls (row 13)
  for (let c = 1; c <= 6; c++)   map[13][c] = WL;
  for (let c = 8; c <= 13; c++)  map[13][c] = WL;
  for (let c = 15; c <= 20; c++) map[13][c] = WL;
  map[13][3] = F; map[13][10] = F; map[13][17] = F;

  // ── Lobby divider (row 15) ────────────────────────────────────────────────
  for (let c = 1; c < W - 1; c++) map[15][c] = WL;
  map[15][11] = F;

  // ── DECOR furniture footprints ────────────────────────────────────────────
  // Room 101 (cols 1–6, rows 1–4): bed, TV, drawing table
  [[1,1],[2,1],[1,2],[2,2]].forEach(([c,r]) => { map[r][c] = DC; });  // bed
  map[1][5] = DC;                                                       // TV
  map[3][4] = DC; map[3][5] = DC;                                       // drawing table

  // Room 102 (cols 8–13, rows 1–4): bed, cabinet, couch
  [[8,1],[9,1],[8,2],[9,2]].forEach(([c,r]) => { map[r][c] = DC; });  // bed
  map[1][12] = DC;                                                      // cabinet
  [[9,3],[10,3],[11,3]].forEach(([c,r]) => { map[r][c] = DC; });      // couch

  // Room 103 (cols 15–20, rows 1–4): bed, shelf1, painting (painting on wall row 0)
  [[15,1],[16,1],[15,2],[16,2]].forEach(([c,r]) => { map[r][c] = DC; }); // bed
  map[1][19] = DC;                                                          // shelf1 bottom tile (top on wall row 0)
  // painting drawn over wall row 0 — no DECOR needed (already non-walkable wall)

  // Room 201 (cols 1–6, rows 9–12): bed, shelf2, travel bag
  [[1,9],[2,9],[1,10],[2,10]].forEach(([c,r]) => { map[r][c] = DC; }); // bed
  map[9][5] = DC;                                                         // shelf2 bottom tile (top on wall row 8)
  map[11][4] = DC;                                                        // travel bag

  // Room 202 (cols 8–13, rows 9–12): bed, TV, drawing table
  [[8,9],[9,9],[8,10],[9,10]].forEach(([c,r]) => { map[r][c] = DC; }); // bed
  map[9][12] = DC;                                                        // TV
  [[9,11],[10,11]].forEach(([c,r]) => { map[r][c] = DC; });             // drawing table

  // Room 203 (cols 15–20, rows 9–12): bed, cabinet, painting, bag
  [[15,9],[16,9],[15,10],[16,10]].forEach(([c,r]) => { map[r][c] = DC; }); // bed
  map[9][19] = DC;                                                            // cabinet
  // painting drawn over wall row 8 — no DECOR needed (already non-walkable wall)
  map[11][15] = DC;                                                            // bag

  // ── Lobby (rows 16–20) ────────────────────────────────────────────────────
  [[8,17],[9,17],[10,17],[11,17],
   [8,18],[9,18],[10,18],[11,18]].forEach(([c,r]) => { map[r][c] = DC; }); // reception counter
  [[2,17],[3,17],[4,17]].forEach(([c,r]) => { map[r][c] = DC; });           // waiting chairs (left)
  [[14,17],[15,17],[16,17]].forEach(([c,r]) => { map[r][c] = DC; });        // couch (right)
  // clock drawn over lobby divider wall row 15 — no DECOR needed
  map[19][19] = DC;  // bag carrier (near entrance)
  [[4,19],[5,19],[6,19]].forEach(([c,r]) => { map[r][c] = DC; }); // second waiting chairs (lower-right)

  return map;
}

function buildKindergartenMap() {
  const W = 22, H = 18;
  const F = INT_TILE.FLOOR, WL = INT_TILE.WALL, D = INT_TILE.DOOR, DC = INT_TILE.DECOR;
  const map = Array.from({ length: H }, () => Array(W).fill(F));

  // Border walls
  for (let c = 0; c < W; c++) { map[0][c] = WL; map[H - 1][c] = WL; }
  for (let r = 0; r < H; r++) { map[r][0] = WL; map[r][W - 1] = WL; }

  // Exit door at bottom centre
  map[H - 1][11] = D;

  // ── DECOR footprints ──────────────────────────────────────────────────────

  // North wall items — row 0 is already WL; only row 1 needs DC
  [1, 2].forEach(c => { map[1][c] = DC; });          // shelf (cols 1-2)
  [4, 5, 6].forEach(c => { map[1][c] = DC; });       // dollhouse (cols 4-6)
  [9, 10, 11].forEach(c => { map[1][c] = DC; });     // dollhouse2 (cols 9-11)
  [15, 16, 17].forEach(c => { map[1][c] = DC; });    // teddybears (cols 15-17)
  [19, 20].forEach(c => { map[1][c] = DC; });        // shelf2 (cols 19-20)

  // Kitchen corner — against west wall (cols 1-4, rows 3-6)
  [[1,3],[2,3],[1,4],[2,4]].forEach(([c,r]) => { map[r][c] = DC; }); // kitchen (2×2)
  [[3,3],[3,4]].forEach(([c,r]) => { map[r][c] = DC; }); // kitchensink (1×2)
  [[4,3],[4,4]].forEach(([c,r]) => { map[r][c] = DC; }); // kitchenstove (1×2)

  // Central carpet (cols 7-12, rows 4-7)
  for (let r = 4; r <= 7; r++)
    for (let c = 7; c <= 12; c++) map[r][c] = DC;

  // Sandbox (cols 15-18, rows 4-6)
  for (let r = 4; r <= 6; r++)
    for (let c = 15; c <= 18; c++) map[r][c] = DC;

  // Left craft table (cols 2-4, rows 10-11)
  for (let r = 10; r <= 11; r++)
    for (let c = 2; c <= 4; c++) map[r][c] = DC;

  // Playmat (cols 7-11, rows 10-12)
  for (let r = 10; r <= 12; r++)
    for (let c = 7; c <= 11; c++) map[r][c] = DC;

  // Right craft table (cols 14-17, rows 10-11)
  for (let r = 10; r <= 11; r++)
    for (let c = 14; c <= 17; c++) map[r][c] = DC;

  return map;
}

function buildNursingHomeMap() {
  const W = 22, H = 16;
  const F = INT_TILE.FLOOR, WL = INT_TILE.WALL, D = INT_TILE.DOOR;
  const map = Array.from({ length: H }, () => Array(W).fill(F));

  // Border walls
  for (let c = 0; c < W; c++) { map[0][c] = WL; map[H - 1][c] = WL; }
  for (let r = 0; r < H; r++) { map[r][0] = WL; map[r][W - 1] = WL; }

  // Exit door at bottom centre
  map[H - 1][11] = D;

  // Vertical room dividers (cols 7 & 14, rows 0–6)
  for (let r = 0; r <= 6; r++) { map[r][7] = WL; map[r][14] = WL; }

  // South walls of resident rooms (row 6) — open archways
  for (let c = 1; c <= 6;  c++) map[6][c] = WL;   // room 1 south wall
  for (let c = 8; c <= 13; c++) map[6][c] = WL;   // room 2 south wall
  for (let c = 15; c <= 20; c++) map[6][c] = WL;  // room 3 south wall
  map[6][4]  = F;   // room 1 opening
  map[6][11] = F;   // room 2 opening
  map[6][18] = F;   // room 3 opening

  // Corridor ↔ lounge divider (row 8) with a central opening
  for (let c = 1; c < W - 1; c++) map[8][c] = WL;
  map[8][11] = F;

  // Nurse's station counter — a solid block cols 1-3, rows 9-10
  [[1,9],[2,9],[3,9],[1,10],[2,10],[3,10]].forEach(([c, r]) => { map[r][c] = WL; });

  // Bed footprints (non-walkable) — 2×2 per room
  [[2,1],[3,1],[2,2],[3,2],
   [9,1],[10,1],[9,2],[10,2],
   [16,1],[17,1],[16,2],[17,2]].forEach(([c, r]) => { map[r][c] = WL; });

  return map;
}

function buildGPClinicMap() {
  const W = 22, H = 20;
  const F = INT_TILE.FLOOR, WL = INT_TILE.WALL, D = INT_TILE.DOOR, DC = INT_TILE.DECOR;
  const map = Array.from({ length: H }, () => Array(W).fill(F));

  // Border walls + exit door
  for (let c = 0; c < W; c++) { map[0][c] = WL; map[H - 1][c] = WL; }
  for (let r = 0; r < H; r++) { map[r][0] = WL; map[r][W - 1] = WL; }
  map[H - 1][11] = D;

  // Vertical room dividers (cols 7 & 14, rows 0-7)
  for (let r = 0; r <= 7; r++) { map[r][7] = WL; map[r][14] = WL; }

  // Room south walls (row 7) with door openings
  for (let c = 1; c <= 6;  c++) map[7][c] = WL;
  for (let c = 8; c <= 13; c++) map[7][c] = WL;
  for (let c = 15; c <= 20; c++) map[7][c] = WL;
  map[7][3]  = F; // Room 1 (Consult A) opening
  map[7][11] = F; // Room 2 (Treatment) opening
  map[7][18] = F; // Room 3 (Consult B) opening

  // ── Room 1 (Consult A) DECOR ─────────────────────────────────────────────
  [[1,1],[2,1],[1,2],[2,2]].forEach(([c,r]) => { map[r][c] = DC; }); // bed.empty (2×2)
  [[3,4],[4,4]].forEach(([c,r]) => { map[r][c] = DC; });             // drawingtable

  // ── Room 2 (Treatment) DECOR ─────────────────────────────────────────────
  [[8,1],[9,1],[8,2],[9,2]].forEach(([c,r]) => { map[r][c] = DC; }); // OPbed (2×2)
  [[10,1],[10,2]].forEach(([c,r]) => { map[r][c] = DC; });           // ultrasound (1×2)
  [[12,1],[13,1],[12,2],[13,2]].forEach(([c,r]) => { map[r][c] = DC; }); // xraymachine (2×2)

  // ── Room 3 (Consult B) DECOR ─────────────────────────────────────────────
  [[15,1],[16,1],[15,2],[16,2]].forEach(([c,r]) => { map[r][c] = DC; }); // bed.empty (2×2)
  [[18,4],[19,4]].forEach(([c,r]) => { map[r][c] = DC; });               // drawingtable

  // ── Waiting area DECOR ───────────────────────────────────────────────────
  [[8,10],[9,10],[10,10],[11,10]].forEach(([c,r]) => { map[r][c] = DC; }); // reception (4×1)
  [[2,12],[3,12],[4,12]].forEach(([c,r]) => { map[r][c] = DC; });          // chairs left
  [[2,15],[3,15],[4,15]].forEach(([c,r]) => { map[r][c] = DC; });          // chairs left lower
  [[14,13],[15,13],[16,13]].forEach(([c,r]) => { map[r][c] = DC; });       // couch right
  map[9][6]   = DC; // plant left of corridor
  map[9][13]  = DC; // plant right of corridor
  map[11][20] = DC; // toilet
  [[15,16],[16,16],[17,16]].forEach(([c,r]) => { map[r][c] = DC; });       // medicineshelf

  return map;
}

// ─── Interior definitions ───────────────────────────────────────────────────

const _hospitalMap     = buildHospitalMap();
const _hospital2FMap   = buildHospital2FMap();
const _pharmacyMap     = buildPharmacyMap();
const _hotelMap           = buildHotelMap();
const _kindergartenMap    = buildKindergartenMap();
const _nursingHomeMap     = buildNursingHomeMap();
const _gpClinicMap        = buildGPClinicMap();

export const INTERIORS = {
  gp: {
    id: "gp",
    name: "Elmwood GP Clinic",
    w: 22,
    h: 20,
    playerEntry: { x: 11, y: 17 },
    exitTile:    { x: 11, y: 19 },
    wallColor:   "#2563eb",
    floorColor:  "#f0f9ff",
    map: _gpClinicMap,
    decorations: [
      // ── Room 1 — Consultation A (cols 1-6, rows 1-6) ─────────────────────
      { image: "/environment/bed.empty.png",         tx: 1,  ty: 1, tw: 2, th: 2 },
      { image: "/environment/medicineshelf2.png",    tx: 4,  ty: 0, tw: 2, th: 1 }, // on north wall
      { image: "/environment/hotel.drawingtable.png",tx: 3,  ty: 4, tw: 2, th: 1 },
      // ── Room 2 — Treatment Room (cols 8-13, rows 1-6) ────────────────────
      { image: "/environment/OPbed.png",             tx: 8,  ty: 1, tw: 2, th: 2 },
      { image: "/environment/ultrasound.png",        tx: 10, ty: 1, tw: 1, th: 2 },
      { image: "/environment/xraymachine.png",       tx: 12, ty: 1, tw: 2, th: 2 },
      // ── Room 3 — Consultation B (cols 15-20, rows 1-6) ───────────────────
      { image: "/environment/bed.empty.png",         tx: 15, ty: 1, tw: 2, th: 2 },
      { image: "/environment/medicineshelf3.png",    tx: 17, ty: 0, tw: 2, th: 1 }, // on north wall
      { image: "/environment/hotel.drawingtable.png",tx: 18, ty: 4, tw: 2, th: 1 },
      // ── Waiting area ──────────────────────────────────────────────────────
      { image: "/environment/reception.png",         tx: 8,  ty: 10, tw: 4, th: 1 },
      { image: "/environment/waitingroomchairs.png", tx: 2,  ty: 12, tw: 3, th: 1 },
      { image: "/environment/waitingroomchairs.png", tx: 2,  ty: 15, tw: 3, th: 1 },
      { image: "/environment/waitingroomchairs.png", tx: 14, ty: 13, tw: 3, th: 1 },
      { image: "/environment/plant1.png",            tx: 6,  ty: 9,  tw: 1, th: 1 },
      { image: "/environment/plant1.png",            tx: 13, ty: 9,  tw: 1, th: 1 },
      { image: "/environment/toilet.png",            tx: 20, ty: 11, tw: 1, th: 1 },
      { image: "/environment/medicineshelf1.png",    tx: 15, ty: 16, tw: 3, th: 1 },
    ],
    examineSpots: [
      // ── Room 1 — Consultation A ───────────────────────────────────────────
      {
        tx: 1, ty: 2,
        name: "🛏️ Examination Couch",
        lines: [
          "The systematic physical examination — inspection, palpation, percussion, auscultation — is the cornerstone of clinical medicine.",
          "In suspected hypertension, measure blood pressure in BOTH arms. A difference of >15 mmHg suggests subclavian artery stenosis. Always confirm with ambulatory or home blood pressure monitoring before diagnosing hypertension.",
          "NICE guidelines (2019): treat hypertension if BP ≥ 140/90 mmHg in clinic AND confirmed by ABPM. First-line: ACE inhibitor (or ARB if intolerant), CCB, or thiazide-like diuretic — tailored by age and ethnicity.",
        ],
      },
      {
        tx: 4, ty: 4,
        name: "💻 Doctor's Desk",
        lines: [
          "The average GP consultation in England is 9.2 minutes. A structured approach — ICE (Ideas, Concerns, Expectations) — maximises the time and improves patient satisfaction.",
          "Safety-netting is essential: always tell the patient what to look for if they get worse. 'If the cough lasts more than 3 weeks, or you develop haemoptysis or weight loss, come back immediately.'",
        ],
      },
      // ── Room 2 — Treatment ────────────────────────────────────────────────
      {
        tx: 8, ty: 2,
        name: "🩺 Treatment Bed",
        lines: [
          "Minor procedures in primary care include: joint injections, coil insertions, skin lesion excision, and suturing. Each carries infection risk — aseptic technique is non-negotiable.",
          "Cellulitis is managed in primary care if non-severe: oral flucloxacillin (or clarithromycin if penicillin-allergic) for 5–7 days. Mark the erythema border with a pen to monitor progression. Admit if systemically unwell or rapidly spreading.",
        ],
      },
      {
        tx: 10, ty: 2,
        name: "🔊 Ultrasound Machine",
        lines: [
          "Point-of-care ultrasound (POCUS) is increasingly available in GP. In primary care it is used for: suspected DVT (compression ultrasound), aortic aneurysm screening, and early pregnancy assessment.",
          "DVT diagnosis: the Wells score stratifies pre-test probability. Low probability + negative D-dimer effectively rules out DVT. High probability requires same-day compression ultrasound. If DVT confirmed: start direct oral anticoagulant (DOAC) — rivaroxaban or apixaban are first-line.",
        ],
      },
      {
        tx: 12, ty: 2,
        name: "📡 X-Ray Machine",
        lines: [
          "Chest X-ray in primary care is used for suspected lung cancer, TB, or heart failure. The two-week-wait (2WW) referral pathway: refer urgently if the CXR is abnormal AND the patient has relevant symptoms (haemoptysis, unexplained weight loss, persistent cough >3 weeks).",
          "Typical CXR findings: consolidation (lobar pneumonia), ground-glass opacification (atypicals, COVID), bilateral hilar lymphadenopathy (sarcoidosis, lymphoma, TB), pleural effusion, cardiomegaly.",
        ],
      },
      // ── Room 3 — Consultation B ───────────────────────────────────────────
      {
        tx: 15, ty: 2,
        name: "🛏️ Examination Couch",
        lines: [
          "Abdominal examination red flags requiring urgent referral: unexplained mass, progressive dysphagia, haematemesis, change in bowel habit >6 weeks (especially in over-50s), unexplained weight loss.",
          "Common GP presentations: IBS (Rome IV criteria: recurrent abdominal pain ≥1 day/week for 3 months, associated with defecation or change in stool frequency/form). Management: dietary advice (low-FODMAP), antispasmodics (mebeverine), loperamide for diarrhoea-predominant.",
        ],
      },
      {
        tx: 18, ty: 4,
        name: "💻 Doctor's Desk",
        lines: [
          "Depression in primary care: use the PHQ-9 to screen. Score ≥10 indicates moderate depression. Step 2: low-intensity interventions (CBT, guided self-help, exercise). Step 3: antidepressants — SSRIs are first-line (sertraline preferred for co-morbidities).",
          "Always ask about suicidal ideation. If risk is present, do not leave the patient alone — refer urgently to crisis team. Document your risk assessment clearly.",
        ],
      },
      {
        tx: 17, ty: 0,
        name: "💊 Antidepressant Shelf",
        lines: [
          "SSRIs are first-line for depression. Sertraline is preferred when co-morbidities exist (safest in cardiac disease and post-MI). Fluoxetine has the longest half-life — useful if adherence is a concern, and safer in overdose. Citalopram and escitalopram are well tolerated but cause QT prolongation at higher doses — avoid in cardiac disease and with other QT-prolonging drugs.",
          "Key SSRI side effects: nausea and headache in the first 2 weeks (warn patients — this is when they are most likely to stop), sexual dysfunction (very common — up to 40%), insomnia or sedation, hyponatraemia (especially in elderly — check sodium at baseline). Serotonin syndrome risk when combined with MAOIs, tramadol, linezolid or triptans: agitation, tremor, tachycardia, hyperthermia — a medical emergency.",
          "Second-line options: SNRIs (venlafaxine, duloxetine) — more effective in severe depression and comorbid anxiety or neuropathic pain, but higher BP at higher doses. Mirtazapine — blocks α2 and H1 receptors; highly sedating (useful if insomnia is prominent), increases appetite and causes weight gain, fewer sexual side effects. TCAs (amitriptyline, nortriptyline) — effective but cardiotoxic in overdose, dangerous in suicide risk. MAOIs (phenelzine) — last resort; risk of hypertensive crisis with tyramine-rich foods (cheese, red wine, cured meats). Review at 2 weeks — if no response by 4–6 weeks at therapeutic dose, switch or refer.",
        ],
      },
      // ── Waiting area ──────────────────────────────────────────────────────
      {
        tx: 8, ty: 10,
        name: "🖥️ Reception Desk",
        lines: [
          "Triage at the GP reception: receptionists are trained to signpost patients using the NHS Pathways tool. However, they must never turn away a patient requesting an urgent same-day appointment if there is any clinical concern.",
          "Common GP workload: musculoskeletal problems (30%), mental health (25%), respiratory (20%), cardiovascular (10%), and skin conditions (8%). Efficient signposting to community pharmacy, physiotherapy, or social prescribing reduces unnecessary consultations.",
        ],
      },
      {
        tx: 2, ty: 12,
        name: "💺 Waiting Chairs",
        lines: [
          "While you wait... the CURB-65 score for community-acquired pneumonia: Confusion, Urea >7 mmol/L, Respiratory rate ≥30, Blood pressure systolic <90 or diastolic ≤60, age ≥65.",
          "Score 0-1: treat at home (oral amoxicillin). Score 2: consider hospital admission. Score 3+: urgent hospital admission. Atypical cover (clarithromycin or doxycycline) should be added for moderate-to-severe CAP.",
        ],
      },
      {
        tx: 15, ty: 16,
        name: "💊 Medicine Display",
        lines: [
          "The most commonly prescribed drugs in UK primary care: atorvastatin (cardiovascular prevention), levothyroxine (hypothyroidism), amlodipine (hypertension), omeprazole (dyspepsia/GORD), and metformin (type 2 diabetes).",
          "Prescribing safety: always check for drug interactions, renal/hepatic dose adjustments, and contraindications. NSAIDs are the most common cause of preventable GP-related drug harm — avoid in elderly, renal impairment, and peptic ulcer disease.",
        ],
      },
    ],
    npcs: [
      {
        id: "gp-receptionist-jo",
        x: 9, y: 9,
        name: "Receptionist Jo",
        sprite: "/blobavatar/blob.nurse.png",
        color: "#2563eb",
        lines: [
          "Welcome to Elmwood GP Clinic! 👋 We're running about 15 minutes behind today — please take a seat.",
          "Don't forget: pharmacists can now manage many minor ailments without an appointment. For UTIs, sore throats, and sinusitis, they're often your quickest option!",
        ],
      },
      {
        id: "gp-dr-ahmed",
        x: 4, y: 5,
        name: "Dr. Ahmed",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#1d4ed8",
        zoneId: "gp",
        triggersBattle: true,
        lines: [
          "Good morning. I've got a patient here with dysuria, frequency and a positive dipstick. Could be a straightforward UTI — but I want to make sure we're not missing anything.",
          "Take a look at the case. What's your management plan?",
        ],
      },
      {
        id: "gp-nurse-rachel",
        x: 11, y: 4,
        name: "Nurse Rachel",
        sprite: "/blobavatar/blob.nurse.png",
        color: "#0891b2",
        triggersGame: "vaccine-children-1",
        lines: [
          "I run the immunisation clinics here — it's one of my favourite parts of the job. Watching those babies get protected against 14 diseases before their first birthday is remarkable.",
          "Fancy a quick tutorial on the UK childhood vaccination schedule?",
        ],
      },
      {
        id: "gp-health-visitor-priya",
        x: 18, y: 3,
        name: "Health Visitor Priya",
        sprite: "/blobavatar/blob.nurse.png",
        color: "#7c3aed",
        triggersGame: "vaccine-children-2",
        lines: [
          "I cover the HPV clinics and advise families on live vaccine safety. Parents often have a lot of questions — especially about which children can't receive certain vaccines.",
          "Want to go through live vaccines, contraindications and the HPV programme?",
        ],
      },
      {
        id: "gp-dr-okonkwo",
        x: 17, y: 14,
        name: "Dr. Okonkwo",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#065f46",
        triggersGame: "vaccine-adults",
        lines: [
          "A lot of adult patients don't realise their vaccinations need topping up — flu every year, shingles in their 70s, and travel vaccines if they're heading abroad.",
          "Let me walk you through the adult vaccination programmes and travel health essentials.",
        ],
      },
      {
        id: "gp-patient-margaret",
        x: 3, y: 14,
        name: "Margaret (Patient)",
        sprite: "/blobavatar/blob.beerman.png",
        color: "#6b7280",
        lines: [
          "I've had this rash on my shin for 3 days — it's spreading and feels warm. My doctor said it might be cellulitis.",
          "She marked the edge with a pen yesterday so we can see if it's spreading. I'm on flucloxacillin — apparently I need to take it an hour before food for it to be properly absorbed. Who knew!",
        ],
      },
    ],
  },
  hospital: {
    id: "hospital",
    name: "City Hospital — ICU",
    w: 22,
    h: 16,
    playerEntry: { x: 11, y: 13 },
    exitTile:    { x: 11, y: 15 },
    wallColor:   "#16a34a",
    floorColor:  "#f0fdf4",
    map: _hospitalMap,
    stairsLinks: [
      { tx: 3, ty: 13, targetId: "hospital2F", targetEntry: { x: 3, y: 12 } },
    ],
    decorations: [
      // Patient beds
      { image: "/environment/bed.patient1.png",      tx: 2,  ty: 1,  tw: 2, th: 2 },
      { image: "/environment/bed.patient2.png",      tx: 9,  ty: 1,  tw: 2, th: 2 },
      { image: "/environment/bed.empty.png",         tx: 15, ty: 1,  tw: 2, th: 2 },
      // Room equipment
      { image: "/environment/ultrasound.png",        tx: 5,  ty: 1,  tw: 1, th: 2 },
      // Lobby — reception centred, chairs on the left
      { image: "/environment/reception.png",         tx: 9,  ty: 10, tw: 4, th: 1 },
      { image: "/environment/waitingroomchairs.png", tx: 2,  ty: 11, tw: 3, th: 1 },
      // Medicine shelves on the right wall
      { image: "/environment/medicineshelf2.png",    tx: 15, ty: 9,  tw: 2, th: 1 },
      { image: "/environment/medicineshelf3.png",    tx: 18, ty: 9,  tw: 2, th: 1 },
      // Amenities — bottom-right corner
      { image: "/environment/shower.png",            tx: 19, ty: 1,  tw: 1, th: 1 },
      { image: "/environment/toilet.png",            tx: 18, ty: 1,  tw: 1, th: 1 },
    ],
    npcs: [
      {
        id: "int-nurse-adebayo",
        x: 11, y: 11,
        name: "Nurse Adebayo",
        sprite: "/blobavatar/blob.nurse.png",
        color: "#22c55e",
        lines: [
          "Welcome to the ICU. Three critical patients are waiting — the doctors will brief you.",
          "Tip: visit each patient room via the corridor. The doctors need your help identifying and treating the pathogens!",
        ],
      },
      {
        id: "int-dr-morrison",
        x: 3, y: 3,
        name: "Dr. Morrison",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#2563eb",
        zoneId: "hospital",
        triggersBattle: true,
        lines: [
          "Patient in Room 1 is deteriorating fast. We've been struggling to identify the pathogen.",
          "Ready to take on the case?",
        ],
      },
      {
        id: "int-dr-zhang",
        x: 10, y: 3,
        name: "Dr. Zhang",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#7c3aed",
        zoneId: "hospital",
        triggersBattle: true,
        lines: [
          "Room 2 — unusual presentation, possibly immunocompromised. We need a sharp diagnosis.",
          "Step in when you're ready.",
        ],
      },
      {
        id: "int-dr-okafor",
        x: 18, y: 3,
        name: "Dr. Okafor",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#dc2626",
        zoneId: "hospital",
        triggersBattle: true,
        lines: [
          "Code amber in Room 3. The team is stumped — this one needs specialist input immediately.",
          "Are you ready to diagnose and treat?",
        ],
      },
    ],
  },
  pharmacy: {
    id: "pharmacy",
    name: "MedCity Pharmacy",
    w: 20,
    h: 14,
    playerEntry: { x: 10, y: 11 },
    exitTile:    { x: 10, y: 13 },
    wallColor:   "#7c3aed",
    floorColor:  "#ede9fe",
    map: _pharmacyMap,
    decorations: [
      // Shelf images drawn over the WALL shelf units
      { image: "/environment/medicineshelf4.png", tx: 1,  ty: 2, tw: 4, th: 2 },
      { image: "/environment/medicineshelf5.png", tx: 6,  ty: 2, tw: 5, th: 2 },
      { image: "/environment/medicineshelf6.png", tx: 12, ty: 2, tw: 6, th: 2 },
      { image: "/environment/medicineshelf7.png", tx: 1,  ty: 5, tw: 4, th: 2 },
      { image: "/environment/medicineshelf4.png", tx: 6,  ty: 5, tw: 5, th: 2 },
      { image: "/environment/medicineshelf6.png", tx: 12, ty: 5, tw: 6, th: 2 },
      // Reception desk
      { image: "/environment/pharmacistreception.png", tx: 6, ty: 8, tw: 4, th: 2 },
      // Plant — left of reception
      { image: "/environment/plant1.png", tx: 5, ty: 8, tw: 1, th: 1 },
    ],
    examineSpots: [
      {
        tx: 2, ty: 3,
        name: "💊 Warfarin",
        lines: [
          "Warfarin is a vitamin K antagonist used for anticoagulation. Regular INR monitoring is essential — the therapeutic range is usually 2–3.",
          "Key interactions: NSAIDs increase bleeding risk. Antibiotics can potentiate warfarin by reducing gut bacteria that produce vitamin K. Always check before adding new drugs.",
        ],
      },
      {
        tx: 8, ty: 3,
        name: "💊 Metformin",
        lines: [
          "Metformin is the first-line oral drug for type 2 diabetes. It reduces hepatic glucose output and improves insulin sensitivity — and does not cause hypoglycaemia when used alone.",
          "Withhold before iodinated contrast media and in renal impairment (eGFR < 30). Lactic acidosis is rare but potentially fatal.",
        ],
      },
      {
        tx: 14, ty: 3,
        name: "💊 Atorvastatin",
        lines: [
          "Atorvastatin is a statin used to lower LDL cholesterol and reduce cardiovascular risk. It inhibits HMG-CoA reductase.",
          "Take at any time of day (unlike simvastatin which should be taken at night). Warn patients about myalgia — rarely, this can progress to rhabdomyolysis. Check CK if muscle pain develops.",
        ],
      },
      {
        tx: 2, ty: 6,
        name: "💊 Amoxicillin",
        lines: [
          "Amoxicillin is a broad-spectrum penicillin antibiotic active against many Gram-positive and some Gram-negative organisms.",
          "Always ask about penicillin allergy before prescribing. A rash during treatment with amoxicillin is very common in glandular fever (EBV infection) — it does not mean the patient is penicillin allergic.",
        ],
      },
      {
        tx: 8, ty: 6,
        name: "💊 Omeprazole",
        lines: [
          "Omeprazole is a proton pump inhibitor (PPI) that irreversibly blocks the H⁺/K⁺ ATPase pump in gastric parietal cells.",
          "Should be co-prescribed with NSAIDs in at-risk patients (elderly, history of peptic ulcer). Long-term use is associated with hypomagnesaemia, increased fracture risk, and C. difficile infection.",
        ],
      },
      {
        tx: 14, ty: 6,
        name: "💊 Ramipril",
        lines: [
          "Ramipril is an ACE inhibitor used for hypertension, heart failure, and renoprotection in diabetic nephropathy.",
          "Monitor potassium and renal function after starting. Do not use in pregnancy. A persistent dry cough affects around 10–15% of patients — switch to an ARB (e.g. losartan) if intolerable.",
        ],
      },
    ],
    npcs: [
      {
        id: "int-pharmacist-lee",
        x: 17, y: 1,
        name: "Pharmacist Lee",
        sprite: "/blobavatar/blob.moneyman.png",
        color: "#f59e0b",
        isShop: true,
        lines: [
          "Welcome to MedCity Pharmacy! 💊",
          "Your coins and XP are stored here. Items and upgrades are coming soon — keep battling to earn more!",
        ],
      },
      {
        id: "int-staff-bob",
        x: 13, y: 1,
        name: "Staff Bob",
        sprite: "/blobavatar/blob.scientist.png",
        color: "#0d9488",
        triggersGame: "med-match-game",
        lines: [
          "Hey! I quiz staff on drug side effects to keep everyone sharp.",
          "Fancy a game? Drag each drug onto its matching side effect profile — let's see how you do!",
        ],
      },
      {
        id: "int-trainee-jane",
        x: 8, y: 1,
        name: "Trainee Jane",
        sprite: "/blobavatar/blob.nurse.png",
        color: "#22c55e",
        triggersGame: "pharmacist-advisor",
        lines: [
          "Hi! I'm Jane, still learning the ropes here.",
          "Want to practise advising some clients? I'll observe and we can score how you do!",
        ],
      },
      {
        id: "int-pharmacist-priya",
        x: 5, y: 11,
        name: "Pharmacist Priya",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#16a34a",
        triggersGame: "constipation-teaching",
        lines: [
          "People think constipation is simple — just eat more fibre! But there are four completely different types of laxative, each working differently.",
          "Want me to take you through them? I'll cover which to use when, the safety points, and the red flags you should never miss.",
        ],
      },
      {
        id: "int-pharmacist-kofi",
        x: 16, y: 8,
        name: "Pharmacist Kofi",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#7c3aed",
        triggersGame: "nausea-teaching",
        lines: [
          "Nausea is tricky — the same symptom can come from completely different places in the nervous system.",
          "Want me to walk you through the anti-emetics? Understanding which pathway causes the nausea tells you which drug to reach for.",
        ],
      },
      {
        id: "int-pharmacist-yemi",
        x: 10, y: 8,
        name: "Pharmacist Yemi",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#0d9488",
        triggersGame: "pain-teaching",
        lines: [
          "Pain is one of the most common things people come to us with — and there are so many different types of medication, each working in a completely different way.",
          "Want me to take you through them? We'll cover paracetamol, NSAIDs, opioids and neuropathic pain drugs — with questions along the way!",
        ],
      },
      {
        id: "int-customer",
        x: 3, y: 8,
        name: "Customer",
        sprite: "/blobavatar/blob.beerman.png",
        color: "#6b7280",
        lines: [
          "Is it amoxicillin or co-amoxiclav for a dental abscess?",
          "It's co-amoxiclav — you need anaerobic cover. Plain amoxicillin misses the anaerobes responsible for most dental infections!",
        ],
      },
    ],
  },
  oldpeoplesHome: {
    id: "oldpeoplesHome",
    name: "Rosewood Care Home",
    w: 22,
    h: 16,
    playerEntry: { x: 11, y: 13 },
    exitTile:    { x: 11, y: 15 },
    wallColor:   "#b45309",
    floorColor:  "#fef3c7",
    map: _nursingHomeMap,
    decorations: [
      { image: "/environment/bed.patient1.png", tx: 2,  ty: 1, tw: 2, th: 2 },
      { image: "/environment/bed.patient2.png", tx: 9,  ty: 1, tw: 2, th: 2 },
      { image: "/environment/bed.empty.png",    tx: 16, ty: 1, tw: 2, th: 2 },
    ],
    npcs: [
      {
        id: "nh-nurse-fatima",
        x: 11, y: 11,
        name: "Nurse Fatima",
        sprite: "/blobavatar/blob.nurse.png",
        color: "#f59e0b",
        lines: [
          "Welcome to Rosewood Care Home. Our residents have complex medication needs — polypharmacy is a real challenge here.",
          "Talk to Dr. Patel and the care team if you'd like to help with the ward rounds!",
        ],
      },
      {
        id: "nh-dr-patel",
        x: 3, y: 3,
        name: "Dr. Patel",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#7c3aed",
        triggersGame: "dementia-cases",
        lines: [
          "We have several residents here with cognitive decline — each case is quite different.",
          "Want to review some dementia cases with me? I'll give you a presentation and we'll work through the diagnosis and management together.",
        ],
      },
      {
        id: "nh-carer-grace",
        x: 10, y: 3,
        name: "Carer Grace",
        sprite: "/blobavatar/blob.nurse.png",
        color: "#0d9488",
        triggersGame: "dementia-cases",
        lines: [
          "I've been looking after people with dementia for years — it's so important to get the diagnosis right because treatment really differs.",
          "Shall we look at a few cases together? Try to identify the type and what we should do.",
        ],
      },
      {
        id: "nh-dr-webb",
        x: 18, y: 3,
        name: "Dr. Webb",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#dc2626",
        triggersGame: "dementia-cases",
        lines: [
          "Dementia is not one disease — the type changes everything: prognosis, treatment, even which drugs to avoid.",
          "Ready to test your knowledge on a few real-world presentations?",
        ],
      },
      {
        id: "nh-dr-chen",
        x: 7, y: 11,
        name: "Dr. Chen",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#6366f1",
        triggersGame: "dementia-teaching-lewy",
        lines: [
          "Hello! I'm Dr. Chen, consultant neurologist. I run teaching sessions here for the care team.",
          "Shall we go through Lewy body dementia and Parkinson's disease dementia? I'll explain the theory and then test you as we go.",
        ],
      },
      {
        id: "nh-dr-mwangi",
        x: 8, y: 13,
        name: "Dr. Mwangi",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#dc2626",
        triggersGame: "dementia-teaching-vascular",
        lines: [
          "Hello! I'm Dr. Mwangi — I specialise in vascular dementia. It's the most preventable form of dementia, yet so often overlooked.",
          "Want to go through it together? We'll cover the subtypes, how to spot it on imaging, and why risk factor control is everything.",
        ],
      },
      {
        id: "nh-dr-osei",
        x: 16, y: 13,
        name: "Dr. Osei",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#0d9488",
        triggersGame: "dementia-teaching-alzheimers",
        lines: [
          "Hi there! I'm Dr. Osei — I cover Alzheimer's and frontotemporal dementia for the team.",
          "Want to go through both? I'll take you through the pathology, clinical features and treatment, with questions along the way.",
        ],
      },
      {
        id: "nh-infection-control",
        x: 5, y: 9,
        name: "Nurse Adaeze",
        sprite: "/blobavatar/blob.nurse.png",
        color: "#dc2626",
        zoneId: "oldpeoplesHome",
        triggersBattle: true,
        lines: [
          "I'm the infection control nurse here at Rosewood. Care homes are hotspots — C. diff, pneumonia, MRSA.",
          "We've got an active case on the ward. Think you can help manage it?",
        ],
      },
      {
        id: "nh-visitor-ann",
        x: 16, y: 11,
        name: "Ann (Visitor)",
        sprite: "/blobavatar/blob.beerman.png",
        color: "#6b7280",
        lines: [
          "My mother is on so many tablets — it's hard to keep track.",
          "Did you know that patients over 75 are often on 5 or more regular medications? That's called polypharmacy, and it significantly increases the risk of side effects and interactions.",
        ],
      },
    ],
  },
  kindergarten: {
    id: "kindergarten",
    name: "Sunshine Kindergarten",
    w: 22,
    h: 18,
    playerEntry: { x: 11, y: 15 },
    exitTile:    { x: 11, y: 17 },
    wallColor:   "#ec4899",
    floorColor:  "#fff0f5",
    map: _kindergartenMap,
    decorations: [
      // ── North wall (ty:0 — rendered on the wall itself) ───────────────────
      { image: "/environment/kindergarden.shelf.png",     tx: 1,  ty: 0, tw: 2, th: 2 },
      { image: "/environment/kindergarden.dollhouse.png", tx: 4,  ty: 0, tw: 3, th: 2 },
      { image: "/environment/kindergarden.dollhouse2.png",tx: 9,  ty: 0, tw: 3, th: 2 },
      { image: "/environment/kindergarden.teddybears.png",tx: 15, ty: 0, tw: 3, th: 2 },
      { image: "/environment/kindergarden.shelf2.png",    tx: 19, ty: 0, tw: 2, th: 2 },
      // ── Kitchen corner (west side) ────────────────────────────────────────
      { image: "/environment/kindergarden.kitchen.png",      tx: 1, ty: 3, tw: 2, th: 2 },
      { image: "/environment/kindergarden.kitchensink.png",  tx: 3, ty: 3, tw: 1, th: 2 },
      { image: "/environment/kindergarden.kitchenstove.png", tx: 4, ty: 3, tw: 1, th: 2 },
      // ── Central play area ─────────────────────────────────────────────────
      { image: "/environment/kindergarden.carpet.png",  tx: 7,  ty: 4, tw: 6, th: 4 },
      { image: "/environment/kindergarden.sandbox.png", tx: 15, ty: 4, tw: 4, th: 3 },
      // ── Lower activity area ───────────────────────────────────────────────
      { image: "/environment/kindergarden.table.png",   tx: 2,  ty: 10, tw: 3, th: 2 },
      { image: "/environment/kindergarden.playmat.png", tx: 7,  ty: 10, tw: 5, th: 3 },
      { image: "/environment/kindergarden.table2.png",  tx: 14, ty: 10, tw: 4, th: 2 },
    ],
    examineSpots: [
      // ── North-wall items (player bumps into row 1 from row 2) ─────────────
      {
        tx: 1, ty: 1,
        name: "📚 Bookshelf",
        lines: [
          "RSV (Respiratory Syncytial Virus) can survive on hard surfaces like books and toys for several hours.",
          "Children touch contaminated objects then rub their eyes or nose. RSV is the #1 cause of bronchiolitis in infants — think wheeze + crackles in a baby under 1.",
          "Palivizumab (a monoclonal antibody) is given monthly to high-risk infants (premature, congenital heart disease) in winter to prevent severe RSV.",
        ],
      },
      {
        tx: 5, ty: 1,
        name: "🏠 Dollhouse",
        lines: [
          "Impetigo is a highly contagious superficial skin infection most common in children aged 2–5.",
          "It is caused by Staphylococcus aureus (bullous impetigo — thin flaccid blisters) or Group A Streptococcus (non-bullous — honey-coloured crusted sores around the nose and mouth).",
          "It spreads by direct skin-to-skin contact and sharing toys. Treatment: topical fusidic acid for localised disease, oral flucloxacillin for widespread infection.",
        ],
      },
      {
        tx: 10, ty: 1,
        name: "🏡 Play House",
        lines: [
          "Hand, Foot and Mouth Disease (HFMD) is caused by Coxsackievirus A16 or Enterovirus 71.",
          "Classic triad: fever, painful oral ulcers, and vesicular rash on the palms and soles. Highly contagious via the faecal-oral route and respiratory droplets.",
          "Most cases are self-limiting. Children should be excluded from nursery until all lesions have crusted over. No specific antiviral — manage with paracetamol and fluids.",
        ],
      },
      {
        tx: 16, ty: 1,
        name: "🧸 Teddy Bears",
        lines: [
          "Scarlet Fever is caused by Group A Streptococcus producing erythrogenic toxin. It peaks in children aged 4–8.",
          "Look for: sudden-onset fever, 'strawberry tongue', 'sandpaper' rash starting on the trunk, and a white perioral pallor. Spread by respiratory droplets — soft toys can harbour the bacterium for days.",
          "Treatment is 10 days of oral penicillin V. Completing the full course is essential to prevent rare complications: rheumatic fever and post-streptococcal glomerulonephritis.",
        ],
      },
      {
        tx: 19, ty: 1,
        name: "🗄️ Storage Shelf",
        lines: [
          "Rotavirus is the leading cause of severe gastroenteritis in children under 5 worldwide — responsible for 200,000 deaths annually.",
          "It spreads by the faecal-oral route and is extremely stable on surfaces. Presentation: profuse watery diarrhoea, vomiting, and fever over 3–8 days.",
          "The oral Rotarix vaccine is given at 8 and 12 weeks in the UK. No antiviral — treatment is oral rehydration solution (ORS). Avoid anti-diarrhoeal agents in children.",
        ],
      },
      // ── Kitchen corner (player approaches from east at col 5) ─────────────
      {
        tx: 4, ty: 4,
        name: "🍳 Play Kitchen",
        lines: [
          "Norovirus is the most common cause of infectious gastroenteritis in the UK, causing ~3 million cases per year.",
          "Spread by faecal-oral route, contaminated food, and vomit aerosols. Just 18 viral particles can cause infection! Symptoms: sudden-onset vomiting and watery diarrhoea, lasting 1–3 days.",
          "Hand washing with soap and water is essential — alcohol gel does NOT kill norovirus. Infected children should be excluded for 48 hours after the last episode of diarrhoea or vomiting.",
        ],
      },
      // ── Sandbox (player approaches from south at row 7) ───────────────────
      {
        tx: 16, ty: 6,
        name: "🏖️ Sandbox",
        lines: [
          "Sandboxes can harbour Toxocara eggs — roundworm larvae from cat or dog faeces deposited in soil.",
          "Children who play in contaminated sand then touch their mouths can develop Toxocariasis. Most cases cause mild fever and cough, but in rare cases larvae migrate to the eye (ocular larva migrans), causing vision loss.",
          "Prevention: cover sandboxes when not in use, enforce regular hand washing, and ensure pets are routinely dewormed. Treatment: albendazole.",
        ],
      },
      // ── Carpet (player approaches from south at row 8) ────────────────────
      {
        tx: 9, ty: 7,
        name: "🎮 Play Carpet",
        lines: [
          "Threadworms (Enterobius vermicularis) are the most common parasitic worm infection in children in the UK.",
          "An infected child scratches the perianal area at night (classic symptom: nocturnal itching), picks up eggs under the fingernails, and spreads them to surfaces, toys, and bedding. Eggs can survive for 2 weeks on surfaces.",
          "Treatment: a single dose of mebendazole or mebendazole, repeated after 2 weeks — treat ALL household members simultaneously. Wash bedding and towels on the first day of treatment.",
        ],
      },
      // ── Left craft table (player approaches from south at row 12) ─────────
      {
        tx: 3, ty: 11,
        name: "🪑 Craft Table",
        lines: [
          "Meningococcal disease (Neisseria meningitidis) peaks in children under 5 and teenagers living in close contact.",
          "Key alert: a non-blanching petechial or purpuric rash alongside fever is a medical emergency — use the 'glass test'. Early signs can mimic flu: fever, headache, stiff neck, photophobia.",
          "The MenACWY and MenB vaccines are part of the UK childhood schedule. Empirical IV ceftriaxone should be given immediately if meningococcal disease is suspected — do not wait for results.",
        ],
      },
      // ── Playmat (player approaches from south at row 13) ─────────────────
      {
        tx: 9, ty: 12,
        name: "🎨 Playmat",
        lines: [
          "Molluscum contagiosum is a poxvirus causing small, pearly-white, umbilicated papules — very common in children.",
          "It spreads by direct skin contact and sharing towels or bath sponges. In children with eczema the lesions can spread widely due to a disrupted skin barrier.",
          "Usually self-limiting over 12–18 months — no treatment is routinely needed. Reassure parents. Avoid squeezing lesions as this spreads the virus.",
        ],
      },
      // ── Right craft table (player approaches from south at row 12) ────────
      {
        tx: 15, ty: 11,
        name: "🖍️ Art Table",
        lines: [
          "Slapped Cheek Disease (Fifth Disease) is caused by Parvovirus B19.",
          "Classic presentation: bright red 'slapped' cheeks, followed by a lacy reticular rash on the trunk and limbs. The child is most infectious BEFORE the rash appears — making exclusion difficult.",
          "Usually benign and self-limiting in children. However, Parvovirus B19 in pregnancy can cause hydrops fetalis. Warn any pregnant contacts to see their GP urgently.",
        ],
      },
    ],
    npcs: [
      {
        id: "kg-teacher-maya",
        x: 11, y: 8,
        name: "Teacher Maya",
        sprite: "/blobavatar/blob.nurse.png",
        color: "#ec4899",
        lines: [
          "Welcome to Sunshine Kindergarten! 🌈 Our little ones are usually full of energy.",
          "Young children are especially vulnerable to infections — their immune systems are still developing. Vaccination is so important at this age!",
        ],
      },
      {
        id: "kg-dr-lewis",
        x: 4, y: 8,
        name: "Dr. Lewis",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#a21caf",
        zoneId: "kindergarten",
        triggersBattle: true,
        lines: [
          "We've had a few children come down with a nasty rash and fever this week.",
          "Can you help identify what's going around? The children's parents are worried.",
        ],
      },
      {
        id: "kg-parent-sarah",
        x: 16, y: 13,
        name: "Parent Sarah",
        sprite: "/blobavatar/blob.beerman.png",
        color: "#f472b6",
        lines: [
          "My daughter had chickenpox last month — it spread through the whole nursery!",
          "VZV spreads by respiratory droplets AND direct contact with the rash. One infectious child can expose everyone. The vaccine is 90% effective — is your child up to date?",
        ],
      },
      {
        id: "kg-nurse-kim",
        x: 5, y: 5,
        name: "School Nurse Kim",
        sprite: "/blobavatar/blob.nurse.png",
        color: "#dc2626",
        triggersGame: "impetigo-teaching",
        lines: [
          "We've had three children this week come in with those telltale honey-coloured crusts around their mouths. Impetigo again! 😔",
          "It spreads so fast in nurseries. Want to go through how to diagnose and manage it properly?",
        ],
      },
      {
        id: "kg-dr-lewis-hfmd",
        x: 13, y: 8,
        name: "Dr. Patel",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#7c3aed",
        triggersGame: "hfmd-teaching",
        lines: [
          "I've just seen two children with blisters on their hands and soles, and ulcers in their mouths. Classic hand, foot and mouth.",
          "Parents always panic when they see the blisters — shall we go through the full picture so you can reassure them confidently?",
        ],
      },
    ],
  },
  hotel: {
    id: "hotel",
    name: "Grand Hotel",
    w: 22,
    h: 22,
    playerEntry: { x: 11, y: 19 },
    exitTile:    { x: 11, y: 21 },
    wallColor:   "#7c5c3c",
    floorColor:  "#f8f5f0",
    map: _hotelMap,
    decorations: [
      // ── Room 101 (top-left) ───────────────────────────────────────────────
      { image: "/environment/hotel.bed.png",          tx: 1,  ty: 1,  tw: 2, th: 2 },
      { image: "/environment/hotel.TV.png",           tx: 5,  ty: 1,  tw: 1, th: 1 },
      { image: "/environment/hotel.drawingtable.png", tx: 4,  ty: 3,  tw: 2, th: 1 },
      // ── Room 102 (top-centre) ─────────────────────────────────────────────
      { image: "/environment/hotel.bed.png",          tx: 8,  ty: 1,  tw: 2, th: 2 },
      { image: "/environment/hotel.cabinate.png",     tx: 12, ty: 1,  tw: 1, th: 1 },
      { image: "/environment/hotel.couch.png",        tx: 9,  ty: 3,  tw: 3, th: 1 },
      // ── Room 103 (top-right) ──────────────────────────────────────────────
      { image: "/environment/hotel.bed.png",          tx: 15, ty: 1,  tw: 2, th: 2 },
      { image: "/environment/hotel.shelf1.png",       tx: 19, ty: 0,  tw: 1, th: 2 },
      { image: "/environment/hotel.painting.png",     tx: 17, ty: 0,  tw: 2, th: 1 },
      // ── Room 201 (bottom-left) ────────────────────────────────────────────
      { image: "/environment/hotel.bed.png",          tx: 1,  ty: 9,  tw: 2, th: 2 },
      { image: "/environment/hotel.shelf2.png",       tx: 5,  ty: 8,  tw: 1, th: 2 },
      { image: "/environment/hotel.travelbag.png",    tx: 4,  ty: 11, tw: 1, th: 1 },
      // ── Room 202 (bottom-centre) ──────────────────────────────────────────
      { image: "/environment/hotel.bed.png",          tx: 8,  ty: 9,  tw: 2, th: 2 },
      { image: "/environment/hotel.TV.png",           tx: 12, ty: 9,  tw: 1, th: 1 },
      { image: "/environment/hotel.drawingtable.png", tx: 9,  ty: 11, tw: 2, th: 1 },
      // ── Room 203 (bottom-right) ───────────────────────────────────────────
      { image: "/environment/hotel.bed.png",          tx: 15, ty: 9,  tw: 2, th: 2 },
      { image: "/environment/hotel.cabinate.png",     tx: 19, ty: 9,  tw: 1, th: 1 },
      { image: "/environment/hotel.painting.png",     tx: 18, ty: 8,  tw: 1, th: 2 },
      { image: "/environment/hotel.bag.png",          tx: 15, ty: 11, tw: 1, th: 1 },
      // ── Lobby ─────────────────────────────────────────────────────────────
      { image: "/environment/hotel.reception.png",        tx: 8,  ty: 17, tw: 4, th: 2 },
      { image: "/environment/hotel.waitingroomchairs.png",tx: 2,  ty: 17, tw: 3, th: 1 },
      { image: "/environment/hotel.waitingroomchairs.png",tx: 4,  ty: 19, tw: 3, th: 1 },
      { image: "/environment/hotel.couch.png",            tx: 14, ty: 17, tw: 3, th: 1 },
      { image: "/environment/hotel.clock.png",            tx: 13, ty: 15, tw: 1, th: 1 },
      { image: "/environment/hotel.bagcarrier.png",       tx: 19, ty: 19, tw: 1, th: 1 },
    ],
    npcs: [
      {
        id: "hotel-receptionist-amelia",
        x: 7,
        y: 17,
        name: "Receptionist Amelia",
        sprite: "/blobavatar/blob.nurse.png",
        color: "#1a3a5c",
        lines: [
          "Welcome to the Grand Hotel! 🏨 Check-in is open.",
          "Did you know that jet lag is caused by disruption to your circadian rhythm? Melatonin can help reset the body clock — especially for eastward travel.",
        ],
      },
      {
        id: "hotel-dr-greene",
        x: 11,
        y: 7,
        name: "Dr. Greene",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#2563eb",
        zoneId: "hotel",
        triggersBattle: true,
        lines: [
          "I'm attending a travel medicine conference here. Fascinating field!",
          "Ready to test your clinical knowledge? Let's see how you do.",
        ],
      },
      {
        id: "hotel-concierge-pascal",
        x: 13,
        y: 19,
        name: "Concierge Pascal",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#b45309",
        lines: [
          "Bonjour! Can I help with anything?",
          "Travel tip: travellers' diarrhoea is most often caused by enterotoxigenic E. coli. Antibiotics are rarely needed — oral rehydration is key.",
        ],
      },
      {
        id: "hotel-nurse-priya",
        x: 4,
        y: 18,
        name: "Nurse Priya",
        sprite: "/blobavatar/blob.nurse2.png",
        color: "#0891b2",
        triggersGame: "norovirus-teaching",
        lines: [
          "Seven guests have been vomiting since last night — and the chef was unwell two days ago. This has norovirus outbreak written all over it! 😰",
          "Want to go through how to manage this properly? Infection control, treatment, the works — it's all critical when an outbreak hits.",
        ],
      },
    ],
  },
  hospital2F: {
    id: "hospital2F",
    name: "City Hospital — Cardiology Ward",
    w: 22,
    h: 16,
    playerEntry: { x: 11, y: 13 },
    wallColor:   "#0369a1",
    floorColor:  "#f0f9ff",
    map: _hospital2FMap,
    stairsLinks: [
      { tx: 3, ty: 13, targetId: "hospital", targetEntry: { x: 3, y: 12 } },
    ],
    decorations: [
      { image: "/environment/bed.patient1.png",   tx: 2,  ty: 1, tw: 2, th: 2 },
      { image: "/environment/xraymachine.png",    tx: 11, ty: 1, tw: 2, th: 2 },
      { image: "/environment/OPbed.png",          tx: 18, ty: 1, tw: 2, th: 2 },
      { image: "/environment/medicineshelf2.png", tx: 1,  ty: 9, tw: 2, th: 1 },
      { image: "/environment/medicineshelf3.png", tx: 4,  ty: 9, tw: 2, th: 1 },
      { image: "/environment/medicineshelf2.png", tx: 7,  ty: 9, tw: 2, th: 1 },
      { image: "/environment/medicineshelf3.png", tx: 13, ty: 9, tw: 2, th: 1 },
      { image: "/environment/medicineshelf1.png", tx: 17, ty: 9, tw: 3, th: 1 },
    ],
    examineSpots: [
      {
        tx: 1, ty: 9,
        name: "💊 Antiepileptic Shelf",
        lines: [
          "Levetiracetam (Keppra) is now first-line for most seizure types — focal and generalised. It has a broad spectrum, few drug interactions, and does not induce hepatic enzymes.",
          "Mechanism: binds SV2A (synaptic vesicle glycoprotein), reducing neurotransmitter release. Renally excreted — dose-reduce in renal impairment. Key side effect: irritability and mood disturbance ('Keppra rage') — warn patients and carers.",
          "Sodium valproate remains highly effective but is CONTRAINDICATED in women of childbearing age unless a pregnancy prevention programme is in place — it causes neural tube defects and neurodevelopmental harm in up to 40% of exposed children.",
        ],
      },
      {
        tx: 4, ty: 9,
        name: "⚠️ Phenytoin Shelf",
        lines: [
          "Phenytoin is an older antiepileptic with a narrow therapeutic index (target range 10–20 mg/L). It follows zero-order (saturation) kinetics — small dose increases cause disproportionately large rises in plasma concentration, making toxicity easy to precipitate.",
          "Toxicity signs — remember NASD: Nystagmus, Ataxia, Slurred speech, Diplopia. At very high levels: encephalopathy and seizures (paradoxically). Chronic use causes gingival hyperplasia, hirsutism, coarsening of facial features, peripheral neuropathy, and osteomalacia.",
          "Phenytoin is a potent CYP450 inducer — it accelerates the metabolism of warfarin, OCP, corticosteroids, and many antiretrovirals. IV phenytoin can cause cardiac arrhythmias — administer slowly (<50 mg/min) with continuous ECG monitoring.",
        ],
      },
      {
        tx: 7, ty: 9,
        name: "🧠 Parkinson's Shelf",
        lines: [
          "Levodopa (combined with carbidopa as co-careldopa, or benserazide as co-beneldopa) remains the most effective treatment for Parkinson's disease. Carbidopa/benserazide are peripheral DOPA decarboxylase inhibitors — they prevent levodopa being converted to dopamine outside the CNS, reducing nausea and allowing a lower dose.",
          "Long-term levodopa complications: wearing-off (end-of-dose deterioration), on-off fluctuations, and dyskinesias (involuntary writhing movements at peak dose). Dopamine agonists (pramipexole, ropinirole) are used as adjuncts or initial monotherapy in younger patients — but carry risks of impulse control disorders (gambling, hypersexuality) in up to 17% of patients.",
          "Never stop dopaminergic medication abruptly — can precipitate neuroleptic malignant syndrome-like akinetic crisis. Anti-emetics in Parkinson's: use domperidone or ondansetron ONLY — metoclopramide and prochlorperazine block D2 receptors and worsen parkinsonism.",
        ],
      },
      {
        tx: 13, ty: 9,
        name: "🩸 Stroke Shelf",
        lines: [
          "Alteplase (tPA) is the thrombolytic used in acute ischaemic stroke. It must be given within 4.5 hours of symptom onset. CT head must be performed first to exclude haemorrhage — giving thrombolysis in haemorrhagic stroke is fatal.",
          "Key contraindications to alteplase: active bleeding, recent surgery or trauma (<14 days), BP >185/110 mmHg (treat first), INR >1.7 or recent anticoagulant use, blood glucose <2.8 or >22 mmol/L, prior stroke within 3 months. Mechanical thrombectomy is superior for large vessel occlusions — refer to a thrombectomy centre within 24 hours if eligible.",
          "Secondary prevention after ischaemic stroke: aspirin 300 mg immediately (after haemorrhage excluded), then clopidogrel 75 mg long-term. Atrial fibrillation-related stroke: anticoagulate with a DOAC (apixaban, rivaroxaban) — start 2 weeks after stroke onset to reduce haemorrhagic transformation risk. Statins (atorvastatin 80 mg) and antihypertensives for all.",
        ],
      },
    ],
    npcs: [
      {
        id: "2f-sister-hayes",
        x: 11, y: 11,
        name: "Sister Hayes",
        sprite: "/blobavatar/blob.nurse.png",
        color: "#0ea5e9",
        lines: [
          "Welcome to the Cardiology Ward — Floor 2. Things are a bit more specialised up here.",
          "The doctors are with patients in the side rooms. Use the stairs in the corner to get back to the ground floor.",
        ],
      },
      {
        id: "2f-dr-thornton",
        x: 3, y: 3,
        name: "Dr. Thornton",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#2563eb",
        zoneId: "hospital",
        triggersBattle: true,
        lines: [
          "Room 1 — chest pain with elevated troponin. ECG is on the desk.",
          "I need a second opinion on the diagnosis. Are you up for it?",
        ],
      },
      {
        id: "2f-dr-yildiz",
        x: 10, y: 3,
        name: "Dr. Yildiz",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#7c3aed",
        zoneId: "hospital",
        triggersBattle: true,
        lines: [
          "Room 2 — atrial fibrillation with a new stroke presentation. Anticoagulation decisions are needed.",
          "Ready to work through the case?",
        ],
      },
      {
        id: "2f-dr-kim",
        x: 18, y: 3,
        name: "Dr. Kim",
        sprite: "/blobavatar/blob.doctor.whitecoat.png",
        color: "#dc2626",
        lines: [
          "Room 3 is for post-MI recovery at the moment — stable but being monitored.",
          "Come back when the cardiology teaching module is ready — I'll be here!",
        ],
      },
    ],
  },
};
